<template>
  <div class="item" v-if="work && !refresh"
       ref="workItem"
       @click="router.push(`/field/works/${work._id}`)"
       :style="isPopoverOpen ? 'z-index: 11;' : ''"
  >
    <div class="content-wrapper">
      <div
          v-if="!imageLoaded && sizeRatio"
          :style="{ paddingTop: sizeRatio, backgroundColor: placeholderColor, position: !imageLoaded ? 'relative' : 'absolute' }"
          class="loader-placeholder"
      ></div>
      <img
          
          @load="onImageLoad"
          @error="onImageError"
          :style="!imageLoaded ? 'position: absolute;' : ''"
          loading="lazy" v-if="!isVimeo && !isVideo && previewImage" :src="previewImage" class="item-image" />
      <video
          @loadedmetadata="onImageLoad"
          @error="onImageError"
          :style="!imageLoaded ? 'position: absolute;' : ''"
          autoplay muted loop v-else-if="!isVimeo && isVideo" :src="previewImage" class="video-preview item-image"></video>
      <div v-else class="vimeo-wrapper pointer-events-none" v-if="videoData" :style="!imageLoaded ? 'position: absolute;' : ''">
        <div :ref="setVimeoRef(0)"></div> <!-- Bind the ref dynamically -->
      </div>
      <div class="info">
        <div class="title">{{ work.title }}</div>
        <div class="tags">{{ work.user.name }}</div>
      </div>
      <img class="multiple" src="/icons/multiple_white.png" v-if="work.contentImages.length > 1" />

    </div>
    <div v-if="user" class="actions">
      <Popover  :width="350" :isOpen="isPopoverOpen" :disabled="isPopoverDisabled" @update:isOpen="updatePopoverState">
        <template #trigger>
          <RoundedIcon
              ref="roundedIcon"
              :class="[isWorkInAnyGallery(work) ? 'selected' : '', 'h-24']"
              src="/icons/plus_black.png"
          />
        </template>
        <ul class="popover-menu">
          <div class="boards">
            <BoardItem
                v-for="gallery in galleries"
                :key="gallery._id"
                :gallery="gallery"
                :work="work"
                :is-added="isWorkInGallery(gallery, work)"
                @highlight="addToHighlight"
                @delete-gallery="delete_gallery"
            ></BoardItem>
          </div>
          <div @click.stop="newBoard" class="create-board flex justify-start items-center">
            <RoundedIcon src="/icons/plus_black.png"></RoundedIcon>

            <span>Create board</span>
          </div>
        </ul>
      </Popover>
    </div>
    <div v-if="user && user._id === work.user._id" class="actions">
      <WorkFunctions :work="work" @reload="emit('item:updated')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {useRouter} from "vue-router";
const router = useRouter();
import {watch, onMounted, ref} from 'vue';
import axios from "axios";
import Popover from "~/components/Popover.vue";
import RoundedIcon from "~/components/RoundedIcon.vue";
import {useUserStore} from "~/composables/useUserStore";
import {useWorkStore} from "~/composables/useWorkStore";
import BoardItem from "~/components/works/BoardItem.vue";
import WorkFunctions from "~/components/works/WorkFunctions.vue";
import {fetchVimeoVideoData} from "~/helper/vimeoPlayerHelper";
import Player from "@vimeo/player";

const {user} = useUserStore();
const { galleries, patchGallery, toggleEditBoardPopup, deleteGallery } = useWorkStore();
const emit = defineEmits(['updated', 'item:updated']);

const workItem = ref<any>(null);
const props = defineProps({
  work: {
    type: Object,
    required: true
  }
});


const refresh = ref(false);
const isPopoverOpen = ref(false);
const isPopoverDisabled = ref(false);
const vimeoRefs = ref<any[]>([]); // Store multiple Vimeo refs dynamically
const playerInstance = ref<{ [key: number]: any }>( {});
const itemWidth = ref(0); // For storing dynamic width
const itemHeight = ref(0); // For storing calculated height based on aspect ratio
const startVal = ref<any>(0);
const endVal = ref<any>(0);
const ACCESS_TOKEN = '2882f8d876e951dfb0357c88629dcf78'; // Replace with your Vimeo access token

const isVimeo = ref(false);
const vimeoEmbedUrl = ref('');

const videoData = ref<any>(null);
const previewImage = ref('');

const imageLoaded = ref(false); // Track if the image has loaded
const sizeRatio = ref(''); // Store the aspect ratio as padding-top percentage
const placeholderColor = '#f0f0f0'; // Default placeholder color

const setVimeoRef = (index: number) => (el: any) => {
  if (el) {
    vimeoRefs.value[index] = el;
  } else {
    delete vimeoRefs.value[index];
  }
};

// Calculate size ratio for the placeholder
const calculateVimeoRatio = (width: number, height: number) => {
  const ratio = (height / width) * 100;
  sizeRatio.value = `${ratio}%`;
};

const calculateItemWidth = () => {
  const itemElement = document.querySelector('.item') as HTMLElement;
  if (itemElement) {
    itemWidth.value = itemElement.offsetWidth;
    console.log('item width', itemWidth.value);
    // Calculate the expected height based on the aspect ratio of the video
    if (videoData.value) {
      itemHeight.value = (itemWidth.value / videoData.value.width) * videoData.value.height;
      calculateVimeoRatio(itemWidth.value, itemHeight.value);
    }
  }
};
const timeToSeconds = (formattedTime: string) => {
  const [minutes, seconds] = formattedTime.split(':').map(Number); // Split and convert to numbers
  return (minutes * 60) + seconds;
};

// Helper function to extract query parameters from URL
const getQueryParams = (url: string) => {
  const urlParams = new URLSearchParams(new URL(url).search);
  const start = urlParams.get('start') || '0';
  const end = urlParams.get('end');

  return {
    start: start,
    end: end ? end : '0',
  };
};

const restartAutoplay = (player: Player) => {
  // Check if the playerInstance already exists to avoid re-initialization

  console.log('res', player);
  if (player) {
    player.ready().then(() => {
      console.log('restart autoplay', startVal.value);
      player.setCurrentTime(startVal.value).then(() => {
        player.play();
      });

      player.on('timeupdate', (data: any) => {
        //console.log('data', data.seconds, endVal.value);
        if (data.seconds >= endVal.value) {
          player.pause();
          player.setCurrentTime(startVal.value).then(() => {
            player.play();
            console.log('played currentTime', startVal.value, workItem.value);
          });
        }
      });
    }).catch((error: any) => {
      console.error('Player is not ready:', error);
    });
  }
};

const updatePopoverState = (newState: any) => {
  isPopoverOpen.value = newState;
};
const isWorkInGallery = (gallery:any, work:any) => {
  const ws = gallery.works.filter(w => !!w.work);
  return ws.some((galleryWork:any) => galleryWork.work._id === work._id);
}
const isWorkInAnyGallery = (work: any) => {
  const gals = galleries.value;

  // Check if the work exists in any of the other galleries
  return gals.some(gallery => isWorkInGallery(gallery, work));
}

const getVimeoVideoId = (url: any) => {
  const regex = /vimeo\.com\/(?:.*#|.*\/videos\/)?([0-9]+)(?:$|\/|\?)/;
  const match = regex.exec(url);
  return match ? match[1] : null;
};

const fetchVideo = async (videoUrl: string) => {
  try {
    const videoId = getVimeoVideoId(videoUrl);
    if (!videoId) {
      return;
    }

    const response = await axios.get(`https://api.vimeo.com/videos/${videoId}`, {
      headers: {
        Authorization: `Bearer 2882f8d876e951dfb0357c88629dcf78` // Replace with your Vimeo access token
      }
    });

    videoData.value = response.data;
    previewImage.value = videoData.value.pictures.sizes[0].link;
  } catch (error) {
    console.error('Vimeo API error:', error);
  }
};

const delete_gallery = async (gallery:any) => {
  await deleteGallery(gallery._id);
  console.log('deletedGal');
  emit('updated');
}
const addToHighlight = async (gallery:any, work:any) => {
  console.log('gallery', gallery);
  const galleryWorkIds = gallery.works.map((work: any) => work.work ? work.work._id : '');
  const coverWorkId = gallery.coverImage ? gallery.coverImage._id : '';

  if (galleryWorkIds.includes(work._id)) {
    const indexOf = galleryWorkIds.indexOf(work._id);
    gallery.works.splice(indexOf, 1);
  } else {
    const workItem = {
      work: work._id,
      isHighlighted: true,
      isCover: work._id === coverWorkId
    }
    gallery.works.push(workItem);
  }
  await saveGallery(gallery);
}

const saveGallery = async (gallery: any) => {
  const ws = gallery.works.filter((w: any) => !!w.work);
  gallery.works = ws;
  const save = await patchGallery(gallery, gallery._id);
  console.log('saved', save);
  emit('updated');
};
const newBoard = async () => {
  toggleEditBoardPopup(null);
};
// Function to extract size from the URL
const extractSizeFromUrl = (url: string) => {
  const regex = /_(\d+)x(\d+)\./;
  const match = url.match(regex);
  if (match) {
    const width = parseInt(match[1], 10);
    const height = parseInt(match[2], 10);
    return { width, height };
  }
  return null;
};

// Calculate size ratio for the placeholder
const calculateSizeRatio = (url: string) => {
  const size = extractSizeFromUrl(url);
  if (size) {
    const ratio = (size.height / size.width) * 100;
    sizeRatio.value = `${ratio}%`;
  }
};

const onImageLoad = async () => {
  await nextTick();
  imageLoaded.value = true; // Hide placeholder when image loads
};

const onImageError = () => {
  console.error('Image failed to load:', previewImage.value);
};

const isVideo = ref(false);

onMounted(async () => {
  if (props.work.coverImage.includes('vimeo')) {
    try {
      isVimeo.value = true;
      videoData.value = await fetchVimeoVideoData(props.work.coverImage, ACCESS_TOKEN); // Fetch Vimeo data
      const videoId = getVimeoVideoId(props.work.coverImage);

      const { start, end } = getQueryParams(props.work.coverImage);
      console.log('vid url', props.work.coverImage, start, end);
      startVal.value = start;
      endVal.value = end;
      await nextTick();
      calculateItemWidth();

      const vimeoRef = vimeoRefs.value[0] as any; // Get the correct Vimeo ref from the list
      console.log('vimeo ref', vimeoRef);

      // Check if playerInstance already exists, if not, create a new one
      if (!playerInstance.value[props.work.id] && vimeoRef) {
        const player = new Player(vimeoRef, {
          id: parseInt(videoId),
          autoplay: startVal.value === '0',
          muted: true,
          loop: true,
          autopause: false,
          controls: false,
          width: itemWidth.value, // Set player width
          height: itemHeight.value // Set player height based on aspect ratio
        });

        player.on('loaded', () => {
          playerInstance.value[props.work.id] = player;
          imageLoaded.value = true;

          restartAutoplay(player);
          console.log('Vimeo player instance created', playerInstance.value[props.work.id]);
        });
      }

      previewImage.value = videoData.value.pictures.sizes[0].link; // Set preview image
    } catch (error) {
      console.error('Vimeo API error:', error);
    }
  } else {
    isVideo.value = props.work.coverImage.includes('.mp4') || props.work.coverImage.includes('.mov');
    previewImage.value = props.work.coverImage;
    calculateSizeRatio(previewImage.value); // Calculate placeholder ratio
  }
});
</script>

<style scoped>


.board-item img {
  width: 40px;
}
.create-board {
  background: #ededed;
  border-radius: 10px;
  color: black;
  padding: 16px 30px;
  gap: 20px;
}
.boards {
  max-height: 312px;
  color: white;
  overflow: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.item {
  display: grid;
  grid-template-rows: 1fr auto;
  overflow: visible;
  position: relative;
  width: 100%;
  cursor: pointer;
}
.item > div:not(.actions):first-child {
  position: relative;
}
.actions .icon {
  mix-blend-mode: difference;
}
.content-wrapper {
  overflow: hidden;
}
.archive {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
  border: 1px solid #dadada;
}
.archive .label {
  font-size: 32px;
  padding: 20px 37px;
  color: black;
}

.item-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 10px;
}

.info {
  position: absolute;
  bottom: 0;
  left: 0;
  color: white;
  padding: 18px;
  width: 100%;
}

.title, .tags {
  margin: 0;
  font-size: 16px;
  line-height: 1.15;
}

.selected {
  background: #E3FF00;
  border: unset;
}
.vimeo-wrapper {
  border-radius: 10px;
  overflow: hidden;
}
.vimeo-wrapper iframe {
  width: 100%;
  height: 100%;
}
.actions {
  position: absolute !important;
  top: 18px;
  right: 18px;
}
.multiple {
  position: absolute;
  top: 18px;
  left: 18px;
  width: 38px;
  height: 38px;
  object-fit: cover;
}
</style>
